import { useAppDispatch, useAppSelector } from '../store/store';
import { useState, useEffect, createContext, useCallback } from 'react';
import { IPerson, selectPersonLoggedIn } from '../store/slices/auth';
import {
  getConversationHistory,
  updateChat,
  updateMessageStatus,
  getParticipantsByConversation,
  getConversations,
  setCurrentConversation,
  setConversationParticipants,
  updateConversationStatus,
  // setSDPOffer,
  setCandidateOffer,
  updateMessagePayload,
} from '../store/slices/chat';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { deleteByPersonIdandConversationId } from '../services/indexedDB';

export const ChatContext = createContext<any>(null);

export const ChatContextProvider = ({ children }: any) => {
  const dispatch = useAppDispatch();
  const personLoggedIn = useAppSelector<IPerson>(selectPersonLoggedIn);
  const { currentConversation } = useAppSelector<any>((state) => state.chat);
  const userProfile = {
    personId: parseFloat(personLoggedIn.person_external_id),
  };

  const messageStatusData = {
    type: 'message_status',
    message_status: 'R',
    person_id: userProfile.personId,
    conversation_id: currentConversation?.conversation_id,
  };

  const [socket, setSocket] = useState<any>(null);
  const [newMessage, setNewMessage] = useState('');
  const [typing, setTyping] = useState(false);
  const [timeOutState, setTimeOutState] = useState<any>(null);
  const [userIsTyping, setUserIsTyping] = useState(null);

  const updateStatus = (conversationHistory: any) => {
    //Check if any of the messages the logged in user have received is unReaded, if so emit a socket for reading the message.
    if (conversationHistory?.length > 0 || currentConversation?.conversation_status !== 'C') {
      const messageIds = conversationHistory
        .filter((el: any) => el.person_id.toString() != personLoggedIn.person_external_id)
        .filter((el: any) =>
          el?.message_status?.find((e: any) => !['R', 'R '].includes(e.current_status))
        )
        .filter((el: any) => el?.message_external_identifier !== null)
        .map((el: any) => el.message_external_identifier);

      messageIds.length > 0 &&
        socket.emit('message_status', { ...messageStatusData, messages_external_ids: messageIds });
    }
  };

  // Setup initial socket
  useEffect(() => {
    if (currentConversation?.conversation_id) {
      const newSocket = io({ path: '/chat/ws' });
      setSocket(newSocket);
      deleteByPersonIdandConversationId(userProfile.personId, currentConversation?.conversation_id);
      setNewMessage('');
      return () => {
        newSocket.disconnect();
        deleteByPersonIdandConversationId(
          userProfile.personId,
          currentConversation?.conversation_id
        );
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversation?.conversation_id]);

  // Recive and Triger web-socket events
  useEffect(() => {
    if (socket && currentConversation?.conversation_id) {
      // Request a Conversation event
      socket.emit('request_conversation', {
        conversation_id: currentConversation.conversation_id,
        person_id: userProfile.personId,
      });
      dispatch(getConversationHistory(currentConversation.conversation_id)).then((data: any) =>
        updateStatus(data.payload)
      );
      dispatch(getParticipantsByConversation(currentConversation.conversation_id));

      socket.on('receive_message', (messageData: any) => {
        dispatch(updateChat(messageData));
        socket.emit('message_status', {
          ...messageStatusData,
          messages_external_ids: [messageData?.message_external_identifier],
        });
      });
      socket.on('receive_message_status', (data: any) => dispatch(updateMessageStatus(data)));
      socket.on('assign_response', (data: any) => {
        dispatch(setConversationParticipants(data.participants));
        dispatch(updateChat(data.assigment));
      });
      socket.on('close_conversation_response', (data: any) => {
        dispatch(updateChat(data));
        dispatch(getConversations({ getFirstConversation: false, type: '' }));
        dispatch(updateConversationStatus());
      });
      socket.on('conversation_info', (data: any) => console.log('conversation_info', data));
      socket.on('participant_joined', (data: any) => {
        // console.log("participant_joined", data)
      });

      socket.on('receive_sdp_offer', (data: any) => {
        // dispatch(setSDPOffer(data.message_offer_payload));
        dispatch(updateChat({ ...data }));
      });
      socket.on('receive_candidate_offer', (data: any) => {
        dispatch(setCandidateOffer(data));
      });
      socket.on('response_close_sdp_offer', (data: any) => {
        dispatch(updateMessagePayload(data));
      });

      // ERROR HANDLER EVENTS
      socket.on('error_sdp_offer', (data: any) => {
        console.log('error_sdp_offer', data);
      });
      socket.on('message_status_error', (data: any) => {});
      socket.on('send_message_error', (data: any) => console.log('send_message_error', data));
      socket.on('request_conversation_error', (data: any) =>
        console.log('request_conversation_error', data)
      );
      socket.on('assign_error', (data: any) => {
        toast.error(data.message, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      });

      // Recive if user is typing
      socket.on('display_typing', (data: any) => {
        if (data.typing === true) setUserIsTyping(data);
        else setUserIsTyping(null);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, userProfile.personId, dispatch]);

  // Send Message handler
  const sendMessage = useCallback(
    async (data: any) => {
      const messageData = {
        ...data,
        conversation_id: currentConversation?.conversation_id,
        person_id: userProfile.personId,
        message_status: [],
      };
      await socket.emit('send_message', messageData);
      dispatch(updateChat(messageData));
      setNewMessage('');

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [userProfile.personId, socket, currentConversation?.conversation_id]
  );

  // Add Chat Participant handler
  const addChatParticipant = async (data: any) => {
    await socket.emit('assign_person', { ...data });
    dispatch(getConversations({ getFirstConversation: false, type: '' })).then((res: any) => {
      dispatch(
        setCurrentConversation(
          // eslint-disable-next-line
          res.payload.data?.find(
            (el: any) => el.conversation_id == currentConversation?.conversation_id
          )
        )
      );
    });
  };

  // Close Conversation handler
  const closeConversation = async (data: any) => {
    await socket.emit('close_conversation', data);
  };

  const timeoutFunction = () => {
    setTyping(false);
    socket.emit('typing', {
      user: {},
      typing: false,
      conversation_id: currentConversation?.conversation_id,
    });
  };

  const handleTyping = () => {
    if (!typing) {
      setTyping(true);
      socket.emit('typing', {
        user: {
          name: personLoggedIn.person_firstname,
          lastName: personLoggedIn.person_lastname,
          person_id: personLoggedIn.person_external_id,
        },
        typing: true,
        conversation_id: currentConversation?.conversation_id,
      });
      setTimeOutState(setTimeout(timeoutFunction, 3000));
    } else {
      clearTimeout(timeOutState);
      setTimeOutState(setTimeout(timeoutFunction, 3000));
    }
  };

  return (
    <ChatContext.Provider
      value={{
        newMessage,
        setNewMessage,
        sendMessage,
        handleTyping,
        userIsTyping,
        addChatParticipant,
        closeConversation,
        socket,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
